import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, HttpError } from '@/lib/api';
import { useMechanics } from '@/hooks/useInterventions';
import { useToast } from '@/hooks/useToast';
import {
  useUsers,
  useCreateUser,
  useUpdateUser,
  type AppUser,
  type UserRole,
} from '@/hooks/useUsers';
import { generatePassword } from '@/lib/password';
import {
  useTemplates,
  useCreateTemplate,
  useUpdateTemplate,
  useDeleteTemplate,
  type InterventionTemplate,
  type UpdateTemplateInput,
} from '@/hooks/useTemplates';
import {
  useAdminNatures,
  useCreateNature,
  useUpdateNature,
  useDeleteNature,
  useReorderNatures,
  type InterventionNature,
} from '@/hooks/useNatures';
import type { Mechanic, TypeAgenda } from '@/lib/types';
import { TYPE_AGENDA_LABELS } from '@/lib/types';

type AtelierConfig = {
  id: string;
  nom: string;
  adresse: string;
  telephone: string;
  email: string;
  dayStartHour: number;
  dayEndHour: number;
  closedDays: number[];
  updatedAt: string;
};

type Holiday = {
  id: string;
  date: string;
  nom: string;
  recurring: boolean;
};

type Tab = 'users' | 'atelier' | 'mecaniciens' | 'templates' | 'natures';

const ROLE_LABELS = {
  admin: 'Admin',
  reception: 'Réception',
  atelier: 'Atelier',
  mecanicien: 'Mécanicien',
} as const;

const WEEKDAY_LABELS = [
  'Dimanche',
  'Lundi',
  'Mardi',
  'Mercredi',
  'Jeudi',
  'Vendredi',
  'Samedi',
];

export default function AdminPage() {
  const [tab, setTab] = useState<Tab>('users');

  return (
    <div className="p-6 space-y-5">
      <header>
        <h1 className="text-xl font-semibold text-text-primary">Administration</h1>
        <p className="text-xs text-text-muted font-mono mt-0.5">Réservé administrateurs</p>
      </header>

      <div className="flex items-center gap-1 border-b border-border-light">
        <TabBtn current={tab} value="users" onClick={setTab}>
          Utilisateurs
        </TabBtn>
        <TabBtn current={tab} value="atelier" onClick={setTab}>
          Atelier
        </TabBtn>
        <TabBtn current={tab} value="mecaniciens" onClick={setTab}>
          Mécaniciens
        </TabBtn>
        <TabBtn current={tab} value="templates" onClick={setTab}>
          Templates
        </TabBtn>
        <TabBtn current={tab} value="natures" onClick={setTab}>
          Natures
        </TabBtn>
      </div>

      {tab === 'users' && <UsersTab />}
      {tab === 'atelier' && <AtelierTab />}
      {tab === 'mecaniciens' && <MecaniciensTab />}
      {tab === 'templates' && <TemplatesTab />}
      {tab === 'natures' && <NaturesTab />}
    </div>
  );
}

function TabBtn({
  current,
  value,
  onClick,
  children,
}: {
  current: Tab;
  value: Tab;
  onClick: (v: Tab) => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
        current === value
          ? 'border-ktm-orange text-ktm-orange'
          : 'border-transparent text-text-muted hover:text-text-secondary'
      }`}
    >
      {children}
    </button>
  );
}

function PasswordField({
  value,
  onChange,
  id,
}: {
  value: string;
  onChange: (v: string) => void;
  id: string;
}) {
  const toast = useToast();
  return (
    <div className="flex gap-2">
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Mot de passe (min 8)"
        className="flex-1 px-3 py-2 border border-border-light rounded-md text-sm font-mono"
        autoComplete="new-password"
      />
      <button
        type="button"
        onClick={() => onChange(generatePassword())}
        className="btn btn-secondary btn-sm shrink-0"
      >
        Générer
      </button>
      <button
        type="button"
        onClick={async () => {
          if (!value) return;
          await navigator.clipboard.writeText(value);
          toast.success('Mot de passe copié');
        }}
        className="btn btn-secondary btn-sm shrink-0"
      >
        Copier
      </button>
    </div>
  );
}

function CreateUserForm({
  mechanics,
  onDone,
}: {
  mechanics: { id: string; prenom: string; nom: string }[];
  onDone: () => void;
}) {
  const createUser = useCreateUser();
  const toast = useToast();
  const [form, setForm] = useState({
    prenom: '',
    nom: '',
    email: '',
    role: 'reception' as UserRole,
    mecanicienId: '',
    password: '',
  });

  const valid =
    form.prenom.trim() &&
    form.nom.trim() &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email) &&
    form.password.length >= 8;

  async function submit() {
    if (!valid) return;
    try {
      await createUser.mutateAsync({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        prenom: form.prenom.trim(),
        nom: form.nom.trim(),
        role: form.role,
        mecanicienId:
          form.role === 'mecanicien' && form.mecanicienId
            ? form.mecanicienId
            : undefined,
      });
      toast.success('Compte créé');
      onDone();
    } catch (err) {
      if (err instanceof HttpError && err.payload.status === 409) {
        toast.error('Email déjà utilisé');
      } else {
        toast.error('Échec de la création');
      }
    }
  }

  return (
    <div className="px-4 py-4 border-b border-border-light bg-surface-bg space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <input
          value={form.prenom}
          onChange={(e) => setForm({ ...form, prenom: e.target.value })}
          placeholder="Prénom"
          className="px-3 py-2 border border-border-light rounded-md text-sm"
        />
        <input
          value={form.nom}
          onChange={(e) => setForm({ ...form, nom: e.target.value })}
          placeholder="Nom"
          className="px-3 py-2 border border-border-light rounded-md text-sm"
        />
        <input
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="Email"
          className="px-3 py-2 border border-border-light rounded-md text-sm"
        />
        <select
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })}
          className="px-3 py-2 border border-border-light rounded-md text-sm"
        >
          <option value="admin">Admin</option>
          <option value="reception">Réception</option>
          <option value="atelier">Atelier</option>
          <option value="mecanicien">Mécanicien</option>
        </select>
        {form.role === 'mecanicien' && (
          <select
            value={form.mecanicienId}
            onChange={(e) => setForm({ ...form, mecanicienId: e.target.value })}
            className="px-3 py-2 border border-border-light rounded-md text-sm col-span-2"
          >
            <option value="">— Lier à un mécanicien (optionnel) —</option>
            {mechanics.map((m) => (
              <option key={m.id} value={m.id}>
                {m.prenom} {m.nom}
              </option>
            ))}
          </select>
        )}
      </div>
      <PasswordField
        id="create-password"
        value={form.password}
        onChange={(v) => setForm({ ...form, password: v })}
      />
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onDone} className="btn btn-secondary btn-sm">
          Annuler
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!valid || createUser.isPending}
          className="btn btn-primary btn-sm"
        >
          {createUser.isPending ? '…' : 'Créer le compte'}
        </button>
      </div>
    </div>
  );
}

function UserRow({
  user,
  mechanics,
}: {
  user: AppUser;
  mechanics: { id: string; prenom: string; nom: string }[];
}) {
  const updateUser = useUpdateUser();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [pw, setPw] = useState('');
  const [form, setForm] = useState({
    prenom: user.prenom,
    nom: user.nom,
    role: user.role,
    mecanicienId: user.mecanicienId ?? '',
    actif: user.actif,
  });

  async function saveEdit() {
    try {
      await updateUser.mutateAsync({
        id: user.id,
        data: {
          prenom: form.prenom.trim(),
          nom: form.nom.trim(),
          role: form.role,
          mecanicienId:
            form.role === 'mecanicien' && form.mecanicienId
              ? form.mecanicienId
              : null,
          actif: form.actif,
        },
      });
      toast.success('Compte mis à jour');
      setEditing(false);
    } catch {
      toast.error('Échec de la mise à jour');
    }
  }

  async function savePassword() {
    if (pw.length < 8) {
      toast.error('Mot de passe : 8 caractères minimum');
      return;
    }
    try {
      await updateUser.mutateAsync({ id: user.id, data: { password: pw } });
      toast.success('Mot de passe réinitialisé — communiquez-le maintenant (non récupérable ensuite)');
      setResetting(false);
      setPw('');
    } catch {
      toast.error('Échec de la réinitialisation');
    }
  }

  async function toggleActive() {
    try {
      await updateUser.mutateAsync({ id: user.id, data: { actif: !user.actif } });
    } catch {
      toast.error('Échec');
    }
  }

  if (editing) {
    return (
      <tr className="border-t border-surface-divider bg-surface-bg">
        <td className="px-4 py-2.5" colSpan={5}>
          <div className="grid grid-cols-2 gap-3">
            <input
              value={form.prenom}
              onChange={(e) => setForm({ ...form, prenom: e.target.value })}
              placeholder="Prénom"
              className="px-3 py-2 border border-border-light rounded-md text-sm"
            />
            <input
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
              placeholder="Nom"
              className="px-3 py-2 border border-border-light rounded-md text-sm"
            />
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })}
              className="px-3 py-2 border border-border-light rounded-md text-sm"
            >
              <option value="admin">Admin</option>
              <option value="reception">Réception</option>
              <option value="atelier">Atelier</option>
              <option value="mecanicien">Mécanicien</option>
            </select>
            {form.role === 'mecanicien' ? (
              <select
                value={form.mecanicienId}
                onChange={(e) => setForm({ ...form, mecanicienId: e.target.value })}
                className="px-3 py-2 border border-border-light rounded-md text-sm"
              >
                <option value="">— Lier à un mécanicien —</option>
                {mechanics.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.prenom} {m.nom}
                  </option>
                ))}
              </select>
            ) : (
              <div />
            )}
            <label className="flex items-center gap-2 text-sm text-text-secondary">
              <input
                type="checkbox"
                checked={form.actif}
                onChange={(e) => setForm({ ...form, actif: e.target.checked })}
              />
              Actif
            </label>
          </div>
          <div className="flex gap-2 justify-end mt-3">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="btn btn-secondary btn-sm"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={saveEdit}
              disabled={updateUser.isPending}
              className="btn btn-primary btn-sm"
            >
              {updateUser.isPending ? '…' : 'Enregistrer'}
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-t border-surface-divider">
      <td className="px-4 py-2.5 font-medium text-text-primary">
        {user.prenom} {user.nom}
      </td>
      <td className="px-4 py-2.5 text-text-secondary font-mono text-xs">{user.email}</td>
      <td className="px-4 py-2.5">{ROLE_LABELS[user.role]}</td>
      <td className="px-4 py-2.5">
        {user.actif ? (
          <span className="text-xs px-1.5 py-0.5 rounded-sm bg-status-termine-bg text-status-termine-text font-bold uppercase">
            Actif
          </span>
        ) : (
          <span className="text-xs px-1.5 py-0.5 rounded-sm bg-status-livre-bg text-status-livre-text font-bold uppercase">
            Inactif
          </span>
        )}
      </td>
      <td className="px-4 py-2.5 text-right whitespace-nowrap">
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-xs text-ktm-orange hover:underline mr-3"
        >
          Éditer
        </button>
        <button
          type="button"
          onClick={() => setResetting((v) => !v)}
          className="text-xs text-ktm-orange hover:underline mr-3"
        >
          Mot de passe
        </button>
        <button
          type="button"
          onClick={toggleActive}
          className="text-xs text-ktm-orange hover:underline"
        >
          {user.actif ? 'Désactiver' : 'Activer'}
        </button>
        {resetting && (
          <div className="mt-2 flex flex-col gap-2 items-end">
            <div className="w-full max-w-md">
              <PasswordField id={`pw-${user.id}`} value={pw} onChange={setPw} />
            </div>
            <button
              type="button"
              onClick={savePassword}
              disabled={updateUser.isPending}
              className="btn btn-primary btn-sm"
            >
              {updateUser.isPending ? '…' : 'Réinitialiser'}
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

function UsersTab() {
  const usersQuery = useUsers();
  const mechanicsQuery = useMechanics();
  const [showCreate, setShowCreate] = useState(false);
  const mechanics = (mechanicsQuery.data ?? []) as { id: string; prenom: string; nom: string }[];

  return (
    <section className="bg-surface-white border border-border-light rounded-lg overflow-hidden">
      <header className="px-4 py-3 border-b border-border-light flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text-primary">
          {usersQuery.data?.length ?? 0} utilisateurs
        </h2>
        <button
          type="button"
          onClick={() => setShowCreate((v) => !v)}
          className="btn btn-primary btn-sm"
        >
          {showCreate ? 'Annuler' : '+ Nouveau compte'}
        </button>
      </header>
      {showCreate && (
        <CreateUserForm mechanics={mechanics} onDone={() => setShowCreate(false)} />
      )}
      <table className="w-full text-sm">
        <thead className="bg-surface-bg">
          <tr className="text-xs uppercase tracking-wider text-text-muted">
            <th className="text-left px-4 py-2">Utilisateur</th>
            <th className="text-left px-4 py-2">Email</th>
            <th className="text-left px-4 py-2">Rôle</th>
            <th className="text-left px-4 py-2">Statut</th>
            <th className="text-right px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {usersQuery.data?.map((u) => (
            <UserRow key={u.id} user={u} mechanics={mechanics} />
          ))}
        </tbody>
      </table>
    </section>
  );
}

function AtelierTab() {
  const [tokenResult, setTokenResult] = useState<string | null>(null);
  const generateToken = useMutation({
    mutationFn: () => api.post<{ token: string }>('/admin/kiosk-token'),
    onSuccess: (d) => setTokenResult(d.token),
  });

  const tvUrl = tokenResult
    ? `${window.location.origin}/tv?token=${tokenResult}`
    : null;

  return (
    <div className="grid grid-cols-2 gap-4">
      <AtelierConfigCard />

      <section className="bg-surface-white border border-border-light rounded-lg p-5">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-text-primary mb-3">
          Écran TV atelier — token kiosk
        </h2>
        <p className="text-sm text-text-secondary mb-3">
          Génère un token long-lived (30 jours) pour afficher l'écran TV en mode kiosk sans authentification.
        </p>
        <button
          type="button"
          onClick={() => generateToken.mutate()}
          disabled={generateToken.isPending}
          className="btn btn-primary btn-sm"
        >
          {generateToken.isPending ? '…' : 'Générer un nouveau token'}
        </button>
        {tvUrl && (
          <div className="mt-3 space-y-2">
            <div className="text-xs text-text-muted">URL à ouvrir sur l'écran TV :</div>
            <div className="px-3 py-2 bg-surface-bg border border-border-light rounded-md font-mono text-xs break-all">
              {tvUrl}
            </div>
            <a
              href={tvUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary btn-sm inline-flex"
            >
              Ouvrir dans un nouvel onglet ↗
            </a>
          </div>
        )}
      </section>

      <section className="col-span-2">
        <HolidaysCard />
      </section>
    </div>
  );
}

function AtelierConfigCard() {
  const toast = useToast();
  const qc = useQueryClient();
  const configQuery = useQuery({
    queryKey: ['admin', 'atelier-config'],
    queryFn: () => api.get<{ config: AtelierConfig }>('/admin/atelier-config'),
    select: (d) => d.config,
  });

  const [form, setForm] = useState<AtelierConfig | null>(null);

  useEffect(() => {
    if (configQuery.data && !form) setForm(configQuery.data);
  }, [configQuery.data, form]);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<AtelierConfig>) =>
      api.patch<{ config: AtelierConfig }>('/admin/atelier-config', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'atelier-config'] });
      toast.success('Paramètres atelier enregistrés');
    },
    onError: (err) => {
      const msg = err instanceof HttpError ? err.payload.message : 'Erreur de sauvegarde';
      toast.error(msg);
    },
  });

  function toggleClosedDay(day: number) {
    if (!form) return;
    const next = form.closedDays.includes(day)
      ? form.closedDays.filter((d) => d !== day)
      : [...form.closedDays, day].sort((a, b) => a - b);
    setForm({ ...form, closedDays: next });
  }

  function submit() {
    if (!form) return;
    updateMutation.mutate({
      nom: form.nom,
      adresse: form.adresse,
      telephone: form.telephone,
      email: form.email,
      dayStartHour: form.dayStartHour,
      dayEndHour: form.dayEndHour,
      closedDays: form.closedDays,
    });
  }

  if (configQuery.isLoading || !form) {
    return (
      <section className="bg-surface-white border border-border-light rounded-lg p-5">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-text-primary mb-3">
          Informations atelier
        </h2>
        <p className="text-sm text-text-muted">Chargement…</p>
      </section>
    );
  }

  return (
    <section className="bg-surface-white border border-border-light rounded-lg p-5">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-text-primary mb-4">
        Informations atelier
      </h2>
      <div className="space-y-3">
        <div className="space-y-1">
          <label className="input-label">Nom</label>
          <input
            type="text"
            value={form.nom}
            onChange={(e) => setForm({ ...form, nom: e.target.value })}
            className="input"
          />
        </div>
        <div className="space-y-1">
          <label className="input-label">Adresse</label>
          <input
            type="text"
            value={form.adresse}
            onChange={(e) => setForm({ ...form, adresse: e.target.value })}
            className="input"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="input-label">Téléphone</label>
            <input
              type="text"
              value={form.telephone}
              onChange={(e) => setForm({ ...form, telephone: e.target.value })}
              className="input"
            />
          </div>
          <div className="space-y-1">
            <label className="input-label">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="input"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="input-label">Ouverture (h)</label>
            <input
              type="number"
              min={6}
              max={12}
              value={form.dayStartHour}
              onChange={(e) => setForm({ ...form, dayStartHour: Number(e.target.value) })}
              className="input"
            />
          </div>
          <div className="space-y-1">
            <label className="input-label">Fermeture (h)</label>
            <input
              type="number"
              min={12}
              max={22}
              value={form.dayEndHour}
              onChange={(e) => setForm({ ...form, dayEndHour: Number(e.target.value) })}
              className="input"
            />
          </div>
        </div>
        <div className="space-y-2">
          <span className="input-label">Jours fermés</span>
          <div className="flex flex-wrap gap-2">
            {WEEKDAY_LABELS.map((label, idx) => {
              const isClosed = form.closedDays.includes(idx);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => toggleClosedDay(idx)}
                  className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${
                    isClosed
                      ? 'border-ktm-orange bg-ktm-orange-light text-ktm-orange'
                      : 'border-border-light bg-surface-white text-text-secondary hover:bg-surface-hover'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={submit}
            disabled={updateMutation.isPending}
            className="btn btn-primary btn-sm"
          >
            {updateMutation.isPending ? '…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </section>
  );
}

function HolidaysCard() {
  const toast = useToast();
  const qc = useQueryClient();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  const holidaysQuery = useQuery({
    queryKey: ['admin', 'holidays', year],
    queryFn: () => api.get<{ holidays: Holiday[] }>(`/admin/holidays?year=${year}`),
    select: (d) => d.holidays,
  });

  const createMutation = useMutation({
    mutationFn: (data: { date: string; nom: string; recurring: boolean }) =>
      api.post<{ holiday: Holiday }>('/admin/holidays', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'holidays'] });
      toast.success('Jour férié ajouté');
    },
    onError: (err) => {
      const msg = err instanceof HttpError ? err.payload.message : 'Erreur ajout';
      toast.error(msg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/admin/holidays/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'holidays'] });
      toast.success('Jour férié supprimé');
    },
    onError: (err) => {
      const msg = err instanceof HttpError ? err.payload.message : 'Erreur suppression';
      toast.error(msg);
    },
  });

  const [form, setForm] = useState({
    date: `${year}-01-01`,
    nom: '',
    recurring: false,
  });

  const recurring = (holidaysQuery.data ?? []).filter((h) => h.recurring);
  const oneOff = (holidaysQuery.data ?? []).filter((h) => !h.recurring);

  function submit() {
    if (!form.date || !form.nom.trim()) return;
    createMutation.mutate(
      { date: form.date, nom: form.nom.trim(), recurring: form.recurring },
      {
        onSuccess: () => {
          setForm({ date: `${year}-01-01`, nom: '', recurring: false });
        },
      },
    );
  }

  return (
    <div className="bg-surface-white border border-border-light rounded-lg p-5">
      <header className="flex items-center justify-between mb-4">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-text-primary">
          Jours fériés
        </h2>
        <div className="flex items-center gap-2">
          <label className="text-xs text-text-muted">Année</label>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value) || currentYear)}
            className="h-8 px-2 w-24 text-sm border border-border-medium rounded-md bg-surface-white focus:outline-none focus:border-ktm-orange"
          />
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">
            Récurrents (chaque année)
          </h3>
          {recurring.length === 0 ? (
            <p className="text-xs text-text-muted italic">Aucun</p>
          ) : (
            <ul className="space-y-1">
              {recurring.map((h) => (
                <HolidayRow
                  key={h.id}
                  holiday={h}
                  onDelete={() => deleteMutation.mutate(h.id)}
                  isDeleting={deleteMutation.isPending}
                />
              ))}
            </ul>
          )}
        </div>
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">
            Ponctuels ({year})
          </h3>
          {oneOff.length === 0 ? (
            <p className="text-xs text-text-muted italic">Aucun</p>
          ) : (
            <ul className="space-y-1">
              {oneOff.map((h) => (
                <HolidayRow
                  key={h.id}
                  holiday={h}
                  onDelete={() => deleteMutation.mutate(h.id)}
                  isDeleting={deleteMutation.isPending}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="border-t border-border-light pt-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">
          Ajouter un jour férié
        </h3>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="input-label">Date</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="input"
            />
          </div>
          <div className="space-y-1 flex-1 min-w-[200px]">
            <label className="input-label">Nom</label>
            <input
              type="text"
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
              placeholder="Ex: Aïd Al Fitr"
              className="input"
            />
          </div>
          <label className="flex items-center gap-2 h-10 cursor-pointer">
            <input
              type="checkbox"
              checked={form.recurring}
              onChange={(e) => setForm({ ...form, recurring: e.target.checked })}
              className="w-4 h-4 accent-ktm-orange"
            />
            <span className="text-sm text-text-secondary">Récurrent</span>
          </label>
          <button
            type="button"
            onClick={submit}
            disabled={createMutation.isPending || !form.nom.trim()}
            className="btn btn-primary btn-sm"
          >
            {createMutation.isPending ? '…' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );
}

function HolidayRow({
  holiday,
  onDelete,
  isDeleting,
}: {
  holiday: Holiday;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  return (
    <li className="flex items-center justify-between gap-2 px-3 py-1.5 bg-surface-bg border border-border-light rounded-sm">
      <div className="min-w-0 flex items-center gap-2">
        <span className="font-mono text-xs text-text-muted whitespace-nowrap">
          {holiday.date}
        </span>
        <span className="text-sm text-text-primary truncate">{holiday.nom}</span>
      </div>
      <button
        type="button"
        onClick={onDelete}
        disabled={isDeleting}
        className="text-text-muted hover:text-status-annule-text text-sm leading-none px-1 shrink-0"
        aria-label="Supprimer"
        title="Supprimer"
      >
        ✕
      </button>
    </li>
  );
}

function MecaniciensTab() {
  const mechanicsQuery = useMechanics();
  const qc = useQueryClient();

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Pick<Mechanic, 'actif' | 'couleur'>> }) =>
      api.patch<{ mecanicien: Mechanic }>(`/admin/mecaniciens/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mecaniciens'] }),
  });

  return (
    <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {(mechanicsQuery.data ?? []).map((m) => (
        <div key={m.id} className="bg-surface-white border border-border-light rounded-lg p-5">
          <div className="flex items-center gap-3 mb-4">
            <span
              className="w-12 h-12 rounded-md flex items-center justify-center text-text-on-orange font-bold text-lg"
              style={{ backgroundColor: m.couleur }}
            >
              {m.prenom.charAt(0)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-base font-semibold text-text-primary truncate">
                {m.prenom} {m.nom}
              </div>
              <div className="text-xs text-text-muted uppercase tracking-wider">{m.specialite}</div>
            </div>
          </div>

          <div className="space-y-3 text-sm">
            <label className="flex items-center justify-between">
              <span className="text-text-muted">Actif</span>
              <input
                type="checkbox"
                checked={m.actif}
                onChange={(e) => update.mutate({ id: m.id, data: { actif: e.target.checked } })}
                className="w-4 h-4 accent-ktm-orange"
              />
            </label>
            <label className="flex items-center justify-between">
              <span className="text-text-muted">Couleur</span>
              <input
                type="color"
                value={m.couleur}
                onChange={(e) => update.mutate({ id: m.id, data: { couleur: e.target.value } })}
                className="w-10 h-7 cursor-pointer border border-border-light rounded-sm"
              />
            </label>
          </div>

          {update.isError && (
            <div className="mt-3 text-xs text-status-annule-text">
              {update.error instanceof HttpError ? update.error.payload.message : 'Erreur'}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}

function TemplatesTab() {
  const toast = useToast();
  const { data: templates, isLoading } = useTemplates(undefined, false);
  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate();
  const deleteMutation = useDeleteTemplate();

  const [form, setForm] = useState({
    nom: '',
    typeAgenda: 'rapide' as TypeAgenda,
    dureeEstimeeMinutes: 30,
    nature: '',
    descriptionDefault: '',
  });

  function submit() {
    if (!form.nom.trim() || !form.nature.trim()) return;
    createMutation.mutate(
      {
        nom: form.nom.trim(),
        typeAgenda: form.typeAgenda,
        dureeEstimeeMinutes: form.dureeEstimeeMinutes,
        nature: form.nature.trim(),
        descriptionDefault: form.descriptionDefault.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success('Template créé');
          setForm({
            nom: '',
            typeAgenda: 'rapide',
            dureeEstimeeMinutes: 30,
            nature: '',
            descriptionDefault: '',
          });
        },
        onError: (err) => {
          const msg = err instanceof HttpError ? err.payload.message : 'Erreur création';
          toast.error(msg);
        },
      },
    );
  }

  const grouped: Record<TypeAgenda, InterventionTemplate[]> = {
    rapide: [],
    mecanique: [],
    lavage: [],
  };
  (templates ?? []).forEach((t) => {
    grouped[t.typeAgenda].push(t);
  });

  return (
    <div className="space-y-5">
      <section className="bg-surface-white border border-border-light rounded-lg p-5">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-text-primary mb-4">
          Nouveau template
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="input-label">Nom</label>
            <input
              type="text"
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
              placeholder="Ex: Vidange complète"
              className="input"
            />
          </div>
          <div className="space-y-1">
            <label className="input-label">Type d'agenda</label>
            <select
              value={form.typeAgenda}
              onChange={(e) => setForm({ ...form, typeAgenda: e.target.value as TypeAgenda })}
              className="input"
            >
              {(['rapide', 'mecanique', 'lavage'] as TypeAgenda[]).map((t) => (
                <option key={t} value={t}>
                  {TYPE_AGENDA_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="input-label">Nature (titre intervention)</label>
            <input
              type="text"
              value={form.nature}
              onChange={(e) => setForm({ ...form, nature: e.target.value })}
              placeholder="Ex: Vidange + filtre"
              className="input"
            />
          </div>
          <div className="space-y-1">
            <label className="input-label">Durée estimée (min)</label>
            <input
              type="number"
              min={15}
              step={15}
              value={form.dureeEstimeeMinutes}
              onChange={(e) =>
                setForm({ ...form, dureeEstimeeMinutes: Number(e.target.value) })
              }
              className="input"
            />
          </div>
          <div className="md:col-span-2 space-y-1">
            <label className="input-label">Description par défaut (optionnel)</label>
            <textarea
              value={form.descriptionDefault}
              onChange={(e) => setForm({ ...form, descriptionDefault: e.target.value })}
              rows={2}
              placeholder="Texte pré-rempli dans le formulaire RDV"
              className="input py-2 h-auto"
            />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={submit}
            disabled={createMutation.isPending || !form.nom.trim() || !form.nature.trim()}
            className="btn btn-primary btn-sm"
          >
            {createMutation.isPending ? '…' : 'Créer le template'}
          </button>
        </div>
      </section>

      {isLoading ? (
        <div className="text-text-muted text-sm">Chargement…</div>
      ) : (
        <div className="space-y-5">
          {(['rapide', 'mecanique', 'lavage'] as TypeAgenda[]).map((type) => {
            const list = grouped[type];
            return (
              <section
                key={type}
                className="bg-surface-white border border-border-light rounded-lg overflow-hidden"
              >
                <header className="px-4 py-3 border-b border-border-light flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-text-primary">
                    {TYPE_AGENDA_LABELS[type]}
                  </h2>
                  <span className="text-xs text-text-muted font-mono">
                    {list.length} template{list.length > 1 ? 's' : ''}
                  </span>
                </header>
                {list.length === 0 ? (
                  <div className="px-4 py-6 text-sm text-text-muted italic text-center">
                    Aucun template pour ce type
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-surface-bg">
                      <tr className="text-xs uppercase tracking-wider text-text-muted">
                        <th className="text-left px-4 py-2">Nom</th>
                        <th className="text-left px-4 py-2">Nature</th>
                        <th className="text-left px-4 py-2">Durée</th>
                        <th className="text-left px-4 py-2">Statut</th>
                        <th className="text-right px-4 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((t) => (
                        <TemplateRow
                          key={t.id}
                          template={t}
                          onUpdate={(data) =>
                            updateMutation.mutate(
                              { id: t.id, data },
                              {
                                onSuccess: () => toast.success('Template mis à jour'),
                                onError: (err) => {
                                  const msg =
                                    err instanceof HttpError
                                      ? err.payload.message
                                      : 'Erreur mise à jour';
                                  toast.error(msg);
                                },
                              },
                            )
                          }
                          onDelete={() => {
                            if (!window.confirm(`Supprimer le template "${t.nom}" ?`)) return;
                            deleteMutation.mutate(t.id, {
                              onSuccess: () => toast.success('Template supprimé'),
                              onError: (err) => {
                                const msg =
                                  err instanceof HttpError
                                    ? err.payload.message
                                    : 'Erreur suppression';
                                toast.error(msg);
                              },
                            });
                          }}
                          isMutating={updateMutation.isPending || deleteMutation.isPending}
                        />
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TemplateRow({
  template,
  onUpdate,
  onDelete,
  isMutating,
}: {
  template: InterventionTemplate;
  onUpdate: (data: UpdateTemplateInput) => void;
  onDelete: () => void;
  isMutating: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    nom: template.nom,
    nature: template.nature,
    dureeEstimeeMinutes: template.dureeEstimeeMinutes,
  });

  function save() {
    onUpdate({
      nom: form.nom.trim(),
      nature: form.nature.trim(),
      dureeEstimeeMinutes: form.dureeEstimeeMinutes,
    });
    setEditing(false);
  }

  if (editing) {
    return (
      <tr className="border-t border-surface-divider bg-surface-bg">
        <td className="px-4 py-2.5">
          <input
            type="text"
            value={form.nom}
            onChange={(e) => setForm({ ...form, nom: e.target.value })}
            className="input h-8 px-2 text-sm"
          />
        </td>
        <td className="px-4 py-2.5">
          <input
            type="text"
            value={form.nature}
            onChange={(e) => setForm({ ...form, nature: e.target.value })}
            className="input h-8 px-2 text-sm"
          />
        </td>
        <td className="px-4 py-2.5">
          <input
            type="number"
            min={15}
            step={15}
            value={form.dureeEstimeeMinutes}
            onChange={(e) =>
              setForm({ ...form, dureeEstimeeMinutes: Number(e.target.value) })
            }
            className="input h-8 px-2 text-sm w-24"
          />
        </td>
        <td className="px-4 py-2.5" />
        <td className="px-4 py-2.5 text-right">
          <button
            type="button"
            onClick={save}
            disabled={isMutating}
            className="text-xs text-ktm-orange hover:underline mr-3"
          >
            Enregistrer
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="text-xs text-text-muted hover:underline"
          >
            Annuler
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-t border-surface-divider">
      <td className="px-4 py-2.5 font-medium text-text-primary">{template.nom}</td>
      <td className="px-4 py-2.5 text-text-secondary">{template.nature}</td>
      <td className="px-4 py-2.5 font-mono text-xs text-text-secondary">
        {template.dureeEstimeeMinutes} min
      </td>
      <td className="px-4 py-2.5">
        <button
          type="button"
          onClick={() => onUpdate({ actif: !template.actif })}
          disabled={isMutating}
          className={`text-xs px-1.5 py-0.5 rounded-sm font-bold uppercase ${
            template.actif
              ? 'bg-status-termine-bg text-status-termine-text'
              : 'bg-status-livre-bg text-status-livre-text'
          }`}
        >
          {template.actif ? 'Actif' : 'Inactif'}
        </button>
      </td>
      <td className="px-4 py-2.5 text-right">
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-xs text-ktm-orange hover:underline mr-3"
        >
          Modifier
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={isMutating}
          className="text-xs text-status-annule-text hover:underline"
        >
          Supprimer
        </button>
      </td>
    </tr>
  );
}

function NaturesTab() {
  const toast = useToast();
  const { data: natures, isLoading } = useAdminNatures();
  const createMutation = useCreateNature();
  const reorderMutation = useReorderNatures();
  const [libelle, setLibelle] = useState('');

  const list = natures ?? [];

  function add() {
    const value = libelle.trim();
    if (!value) return;
    createMutation.mutate(value, {
      onSuccess: () => {
        toast.success('Nature ajoutée');
        setLibelle('');
      },
      onError: (err) => {
        const msg = err instanceof HttpError ? err.payload.message : 'Erreur ajout';
        toast.error(msg);
      },
    });
  }

  function move(index: number, dir: -1 | 1) {
    const next = [...list];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    reorderMutation.mutate(
      next.map((n) => n.id),
      {
        onError: (err) => {
          const msg = err instanceof HttpError ? err.payload.message : 'Erreur réordonnancement';
          toast.error(msg);
        },
      },
    );
  }

  return (
    <div className="space-y-5">
      <section className="bg-surface-white border border-border-light rounded-lg p-5">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-text-primary mb-4">
          Ajouter une nature
        </h2>
        <div className="flex items-end gap-3">
          <div className="space-y-1 flex-1">
            <label className="input-label">Libellé</label>
            <input
              type="text"
              value={libelle}
              onChange={(e) => setLibelle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') add();
              }}
              placeholder="Ex: Révision périodique"
              className="input"
              maxLength={200}
            />
          </div>
          <button
            type="button"
            onClick={add}
            disabled={createMutation.isPending || !libelle.trim()}
            className="btn btn-primary btn-sm"
          >
            {createMutation.isPending ? '…' : 'Ajouter'}
          </button>
        </div>
      </section>

      <section className="bg-surface-white border border-border-light rounded-lg overflow-hidden">
        <header className="px-4 py-3 border-b border-border-light flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text-primary">
            {list.length} nature{list.length > 1 ? 's' : ''}
          </h2>
          <span className="text-xs text-text-muted font-mono">
            Affichées dans le modal RDV (actives uniquement)
          </span>
        </header>
        {isLoading ? (
          <div className="px-4 py-6 text-sm text-text-muted">Chargement…</div>
        ) : list.length === 0 ? (
          <div className="px-4 py-6 text-sm text-text-muted italic text-center">
            Aucune nature — ajoutez-en une ci-dessus.
          </div>
        ) : (
          <ul className="divide-y divide-surface-divider">
            {list.map((n, i) => (
              <NatureRow
                key={n.id}
                nature={n}
                isFirst={i === 0}
                isLast={i === list.length - 1}
                onMoveUp={() => move(i, -1)}
                onMoveDown={() => move(i, 1)}
                disabled={reorderMutation.isPending}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function NatureRow({
  nature,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  disabled,
}: {
  nature: InterventionNature;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  disabled: boolean;
}) {
  const toast = useToast();
  const updateMutation = useUpdateNature();
  const deleteMutation = useDeleteNature();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(nature.libelle);

  useEffect(() => {
    if (!editing) setValue(nature.libelle);
  }, [nature.libelle, editing]);

  function saveLibelle() {
    const next = value.trim();
    if (!next || next === nature.libelle) {
      setEditing(false);
      setValue(nature.libelle);
      return;
    }
    updateMutation.mutate(
      { id: nature.id, data: { libelle: next } },
      {
        onSuccess: () => {
          toast.success('Nature renommée');
          setEditing(false);
        },
        onError: (err) => {
          const msg = err instanceof HttpError ? err.payload.message : 'Erreur renommage';
          toast.error(msg);
          setValue(nature.libelle);
        },
      },
    );
  }

  function toggleActif() {
    updateMutation.mutate(
      { id: nature.id, data: { actif: !nature.actif } },
      {
        onSuccess: () => toast.success(nature.actif ? 'Nature désactivée' : 'Nature activée'),
        onError: (err) => {
          const msg = err instanceof HttpError ? err.payload.message : 'Erreur';
          toast.error(msg);
        },
      },
    );
  }

  function remove() {
    if (!window.confirm(`Supprimer la nature "${nature.libelle}" ?`)) return;
    deleteMutation.mutate(nature.id, {
      onSuccess: () => toast.success('Nature supprimée'),
      onError: (err) => {
        const msg = err instanceof HttpError ? err.payload.message : 'Erreur suppression';
        toast.error(msg);
      },
    });
  }

  const busy = updateMutation.isPending || deleteMutation.isPending || disabled;

  return (
    <li className="flex items-center gap-3 px-4 py-2.5">
      <div className="flex flex-col">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={isFirst || busy}
          className="text-text-muted hover:text-text-primary disabled:opacity-30 leading-none text-xs"
          aria-label="Monter"
        >
          ▲
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={isLast || busy}
          className="text-text-muted hover:text-text-primary disabled:opacity-30 leading-none text-xs"
          aria-label="Descendre"
        >
          ▼
        </button>
      </div>

      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveLibelle();
              if (e.key === 'Escape') {
                setEditing(false);
                setValue(nature.libelle);
              }
            }}
            className="input h-8 px-2 text-sm"
            maxLength={200}
            autoFocus
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-sm text-text-primary hover:underline text-left truncate w-full"
            title="Cliquer pour renommer"
          >
            {nature.libelle}
          </button>
        )}
      </div>

      {editing ? (
        <button
          type="button"
          onClick={saveLibelle}
          disabled={busy}
          className="text-xs text-ktm-orange hover:underline"
        >
          Enregistrer
        </button>
      ) : (
        <>
          <button
            type="button"
            onClick={toggleActif}
            disabled={busy}
            className={`text-xs px-1.5 py-0.5 rounded-sm font-bold uppercase ${
              nature.actif
                ? 'bg-status-termine-bg text-status-termine-text'
                : 'bg-status-livre-bg text-status-livre-text'
            }`}
          >
            {nature.actif ? 'Actif' : 'Inactif'}
          </button>
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            className="text-xs text-status-annule-text hover:underline"
          >
            Supprimer
          </button>
        </>
      )}
    </li>
  );
}
